import React, { useMemo } from 'react';
import { ForgeCard } from '@tylertech/forge-react';
import { useSortable } from '@dnd-kit/sortable';
import cx from 'classnames';
import { uniqueId } from 'lodash';
import { I18n } from 'common/i18n';
import { DragHandleIcon } from './DragHandleIcon';
import { DragDropElementWrapper } from './index';
import { getSortableStyle } from './sortableStyles';

const scope = 'common';

interface SortableForgeCardElementProps<T = unknown> {
  item: DragDropElementWrapper<T>;
  isDragOverlay?: boolean;
  iconName?: string;
}

export const SortableForgeCardElement = <T = unknown,>({
  item,
  isDragOverlay = false,
  iconName = 'drag_horizontal',
}: SortableForgeCardElementProps<T>) => {
  // Generate a stable default ID if none provided
  const sortableId = useMemo(() => item.id || uniqueId('sortable-forge-card-'), [item.id]);
  const ariaLabel = I18n.t('drag_to_reorder_aria_label', { scope });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: {
      type: 'sortable-item',
      item: item,
    },
  });

  const style = getSortableStyle({
    transform,
    transition,
    isDragging,
    isDragOverlay,
  });

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx('sortable-item-wrapper', { 'is-dragging': isDragging })}
    >
      <ForgeCard
        {...item.additionalItemProps}
        id={`drag-element-${item.id}`}
        className={cx('drag-element', item.className)}
        outlined
      >
        <div className="forge-card">
          <div
            {...attributes}
            {...listeners}
            className="drag-handle dnd-kit-drag-handle"
            aria-label={ariaLabel}
          >
            <DragHandleIcon name={iconName} isDraggable={item.dragAndDroppable} />
          </div>
          {item.element}
        </div>
      </ForgeCard>
    </div>
  );
};
